import React, { useRef, useState } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { useShowToast } from '@/hooks';
import { useEditScoreCardMutation } from '@/store/query/interviews';
import { Box as _Box, BoxProps, Flex as _Flex } from '@chakra-ui/react';
import { useScorecardFormHelper } from './useScorecardFormHelper';
import { OverallEvaluationBanner } from './OverallEvaluationBanner';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { ScorecardAutoSave } from '@/components/ats/collect-score-card/AutoSaveScorecard';
import { useScorecardFillWithAI } from './useScorecardFillWithAI';
import { useGetInterviewTranscriptQuery } from '@/store/query/interview';
import {
  AIFillScorecardLoader,
  FillWithAIHeader,
  ScorecardFooter,
} from './components';
import { InterviewType, ScorecardType } from '../types/interview';
import './interviewScorecard.css';

// Chakra 2.10 + TypeScript 4.9 trip TS2590 ("union type too complex") when
// style-prop-heavy components are composed in a single JSX tree. Collapsing
// the components to React.ComponentType<any> kills the inference cascade.
// Runtime behavior is unchanged; Chakra style-prop IntelliSense is suppressed
// in this file only.
type CC = React.ComponentType<any>;
const Box = _Box as CC;
const Flex = _Flex as CC;

export const InterviewScorecardForm = ({
  scorecard,
  interview,
  onSuccess = () => {},
  formContainerStyles = {},
  scorecardFooterStyles = {},
}: {
  scorecard: ScorecardType;
  interview: InterviewType;
  onSuccess: () => void;
  formContainerStyles?: BoxProps;
  scorecardFooterStyles?: BoxProps;
}) => {
  const { can_edit } = scorecard;

  const updateAttachmentChanges = (attachmentDetails) => {
    const type = attachmentDetails.type;
    const attachments = attachmentDetails.attachments;
    const attachmentKey = attachmentDetails.attachmentKey;
    const attachmentId = attachmentDetails.attachmentId;

    // eslint-disable-next-line react-hooks/immutability -- setInitialValues is assigned after useState below
    setInitialValues((prev: any) => {
      const newValues = { ...formRef?.current?.values };

      if (type === 'add' && attachments && attachmentKey) {
        newValues[attachmentKey] = [
          ...(prev[attachmentKey] || []),
          ...attachments,
        ];
      } else if (type === 'remove' && attachmentKey && attachmentId) {
        newValues[attachmentKey] = (prev[attachmentKey] || []).filter(
          (attachment) => attachment.id !== attachmentId
        );
      } else if (type === 'updateError' && attachments && attachmentKey) {
        // Mark the failed files with hasError flag
        const currentAttachments = prev[attachmentKey] || [];
        newValues[attachmentKey] = currentAttachments.map((file) => {
          // Check if this file is in the failed files list (match by name and size)
          const isFailed = attachments.some(
            (failedFile) =>
              failedFile.name === file.name &&
              failedFile.size === file.size &&
              file.hasError
          );
          return isFailed ? { ...file, hasError: true } : file;
        });
      }

      return newValues;
    });
  };

  const { getScoreCardForm, getInitialValues } = useScorecardFormHelper(
    updateAttachmentChanges
  );

  const currUser = useSelector(selectCurrUser);
  const isSomeOtherUserHasDraftScorecard =
    scorecard?.status === 'draft' &&
    scorecard?.submitted_by?.id &&
    scorecard?.submitted_by?.id !== currUser?.id;

  const { data: transcriptData } = useGetInterviewTranscriptQuery(
    { interviewId: interview?.id },
    { skip: !interview?.id || interview?.transcript_status !== 'completed' }
  );
  const [updateScorecard, { isLoading: isUpdating }] =
    useEditScoreCardMutation();

  const [initialValues, setInitialValues] = useState(
    getInitialValues({
      scorecard,
      isSubmitted: isSomeOtherUserHasDraftScorecard ? false : true,
    })
  );
  const [formData, setFormData] = useState(
    getScoreCardForm({ scorecard, isEdit: can_edit })
  );
  const [isAiPolling, setIsAiPolling] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [formKey, setFormKey] = useState(0);
  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: scorecard?.ats_application_id,
    scorecardId: scorecard?.id,
    questions: scorecard?.questions,
  });

  const formRef = useRef(null);
  const toast = useShowToast();
  const { id, ats_application_id } = scorecard || {};

  const { handleFillWithAi, isFilling, isPolling } = useScorecardFillWithAI({
    ats_application_id,
    scorecardId: id,
    interviewId: interview?.id,
    description: description,
    onSuccess: (newScorecard, isError) => {
      if (!isError && newScorecard) {
        onClear();
        const newValues = getInitialValues({
          scorecard: newScorecard,
          isSubmitted: true,
        });
        setInitialValues(newValues);
        // Insert a placeholder for OverallEvaluationBanner before the last item in formData
        setFormData((prevFormData) => {
          const secondLastItem = prevFormData[prevFormData.length - 2];
          if (secondLastItem?.name !== 'OverallEvaluationBanner') {
            return [
              ...prevFormData.slice(0, -1),
              {
                type: 'custom',
                name: 'OverallEvaluationBanner',
                Field: OverallEvaluationBanner,
              },
              prevFormData[prevFormData.length - 1],
            ];
          }
          return prevFormData;
        });
      }
      setIsAiPolling(false);
    },
    onStart: () => {
      setIsAiPolling(true);
    },
  });

  const onClear = () => {
    // Clear all form fields including individual question answers and set the initial values
    const clearedValues: Record<string, any> = {};
    if (scorecard?.questions) {
      scorecard.questions.forEach((_, idx) => {
        clearedValues[idx + 1] = '';
      });
    }
    clearedValues.feedback = '';
    clearedValues.rating = undefined;
    setInitialValues(clearedValues);

    // Create a modified scorecard with cleared values for form regeneration
    // This ensures rating options styling is reset to default state
    const clearedScorecard = {
      ...scorecard,
      rating: undefined,
      feedback: '',
      questions: scorecard.questions?.map((q) => ({ ...q, answer: '' })),
    };

    // Regenerate form configuration with cleared scorecard data
    setFormData(
      getScoreCardForm({ scorecard: clearedScorecard, isEdit: can_edit })
    );

    // Force FormBuilder component to completely re-mount by updating key
    setFormKey((prev) => prev + 1);

    // Reset Formik's internal state (errors, touched states, etc.)
    formRef.current?.resetForm();
  };

  const handleFormSubmit = (values) => {
    const questions = scorecard?.questions?.map((item, idx) => {
      const questionObj: { question: string; answer?: string } = {
        question: item?.question,
      };

      const answer = values?.[idx + 1];
      if (answer) questionObj.answer = answer;

      return questionObj;
    });

    const payload = {
      rating: values?.rating,
      feedback: values?.feedback,
      status: 'submitted',
      questions,
    };

    updateScorecard({
      scorecardId: id,
      payload,
      applicantId: ats_application_id,
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to submit scorecard`,
          description: message,
          status: 'error',
        });
      } else {
        toast({ title: `Scorecard submitted successfully` });
        onSuccess();
      }
    });
  };

  const isEmptyTranscript = transcriptData?.transcript?.length === 0;

  const renderContent = () => {
    if (isFilling || isPolling || isAiPolling) {
      return <AIFillScorecardLoader />;
    }

    return (
      <FormBuilder
        key={formKey}
        config={formData}
        formRef={formRef}
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        hasFormChild={true}
        FormChild={() =>
          scorecard?.status !== 'submitted' ? (
            <ScorecardAutoSave onAutoSave={handleAutoSave} />
          ) : null
        }
      />
    );
  };

  return (
    <Flex flexDir="column" w="100%" h="100%" overflow="hidden">
      {interview?.transcript_status === 'completed' && (
        <FillWithAIHeader
          description={description}
          isEmptyTranscript={isEmptyTranscript}
          isLoading={isFilling || isPolling || isAiPolling}
          handleFillWithAi={handleFillWithAi}
          setDescription={setDescription}
        />
      )}

      <Box flex="1" overflowY="auto" p="28px" {...formContainerStyles}>
        {renderContent()}
      </Box>

      {can_edit && !isFilling && !isPolling && !isAiPolling && (
        <ScorecardFooter
          isSaved={isSaved}
          isUpdating={isUpdating}
          formRef={formRef}
          scorecard={scorecard}
          onClear={onClear}
          scorecardFooterStyles={scorecardFooterStyles}
        />
      )}
    </Flex>
  );
};
